import { React, useEffect, useState } from 'react';

import { Button, Grid, Paper } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';

const LancarAcordo = () => {
    const [documento, setDocumento] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [parcelas, setParcelas] = useState('');
    const [multa, setMulta] = useState(true);
    const [tipo, setTipo] = useState('boleto');
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const columns = [
        {
            field: 'cc',
            headerName: 'CCs',
            width: 150
        }
    ];

    const fetchCCs = async (e) => {
        if (!documento) return;
        const body = JSON.stringify({ documento });
        const response = await fetchFlask('getCCsAgreement', body);
        if (response.ccs) {
            const newRows = response.ccs.map((cc, index) => ({
                id: rows.length + index + 1,
                cc
            }));
            setRows(newRows);

            const allIds = newRows.map(row => row.id);
            setSelectedRows(allIds);
        } else {
            setMessage(response.message || 'Erro ao buscar CCs');
            setStatus('error');
            setOpenAlerta(true);
        }
    };

    useEffect(() => {
        setSelectedRows(rows.map(row => row.id));
    }, [rows]);

    async function submitLancarAcordo(e) {
        e.preventDefault();
        setIsSubmit(true);
        let vencimento = '';
        if (dateTime) {
            setError(false);
            vencimento = dateTime.toISOString().slice(0, 10);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const selectedCCs = rows
            .filter((row) => selectedRows.includes(row.id))
            .map((row) => row.cc);

        const body = JSON.stringify({
            documento,
            vencimento,
            parcelas,
            multa,
            tipo,
            ccs: selectedCCs
        });

        const response = await fetchFlask('gerarLancamentosAcordo', body);
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
        if (response.status === 'success') {
            setDocumento('');
            setDateTime(null);
            setParcelas('');
        }
        setIsSubmit(false);
    };

    return (
        <Box className='mainBox'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <Typography variant='h3'>
                Lançar Acordo
            </Typography>

            <form action='' method='POST' id='formAcordo' onSubmit={submitLancarAcordo}>
                <Grid container>
                    <Grid item xs={6}>
                        <Paper elevation={2} sx={{ padding: 4, marginTop: '20px', width: '100%' }}>
                            <Grid container spacing={2} justifyContent="center">
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <FormControl>
                                            <TextField
                                                required
                                                label='CPF/CNPJ'
                                                value={documento}
                                                inputProps={{ maxLength: 18 }}
                                                onChange={(e) => setDocumento(maskCpfCnpj(e.target.value))}
                                            />
                                        </FormControl>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Button variant='contained' onClick={fetchCCs}>Buscar CCs</Button>
                                    </Box>
                                </Grid>

                                <Grid item xs={6}>
                                    <Box>
                                        <DataGrid
                                            rows={rows}
                                            columns={columns}
                                            checkboxSelection
                                            onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
                                            rowSelectionModel={selectedRows}
                                            localeText={{ noRowsLabel: 'Sem Unidades' }}
                                            hideFooter
                                            autoHeight
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <FormControl>
                                            <TextField
                                                required
                                                label='Número de parcelas'
                                                type='number'
                                                inputProps={{ step: 1, min: 1 }}
                                                value={parcelas}
                                                onChange={(e) => setParcelas(e.target.value)}
                                            />
                                        </FormControl>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <FormControl>
                                            <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    id='data'
                                                    label='Primeiro Vencimento'
                                                    minDate={new Date('2024-10-01')}
                                                    value={dateTime}
                                                    onChange={(value) => setDateTime(value)}
                                                    slotProps={{
                                                        openPickerButton: {
                                                            color: 'primary'
                                                        }
                                                    }}
                                                />
                                                {error && (
                                                    <Typography variant='caption' color='red'>
                                                        Por favor selecione uma data!
                                                    </Typography>
                                                )}
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <FormControlLabel
                                            label={tipo === 'boleto' ? 'BOLETOS' : 'PIX'}
                                            control={
                                                <Switch
                                                    checked={tipo === 'boleto'}
                                                    onChange={
                                                        (e) => {
                                                            setTipo(e.target.checked ? 'boleto' : 'pix');
                                                            if (!e.target.checked) {
                                                                setParcelas(1);
                                                            }
                                                        }}
                                                />
                                            }
                                        />

                                        <FormControlLabel
                                            label='INCLUIR MULTA E JUROS'
                                            control={
                                                <Checkbox
                                                    checked={multa}
                                                    onChange={(e) => { setMulta(e.target.checked); }}
                                                />
                                            }
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <FormControl>
                                            <SubmitButton disabled={selectedRows.length === 0} isSubmit={isSubmit}>LANÇAR ACORDO</SubmitButton>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </form >
        </Box >
    );
};

export default LancarAcordo;
